import React from 'react';
import { createRoot } from 'react-dom/client';
import ChatWidget from '../components/ChatWidget';
import type { PlatformConfig } from '../types';
import '../index.css';

// Default API URL - can be overridden via config
const DEFAULT_API_URL = 'https://crymadx-ai-agent-backend-production.up.railway.app';

interface WidgetConfig {
  siteId: string;
  env?: 'development' | 'production';
  apiUrl?: string;
}

interface CrymadXWidgetAPI {
  init: (config: WidgetConfig) => Promise<void>;
  destroy: () => void;
  isInitialized: boolean;
}

class CrymadXWidget implements CrymadXWidgetAPI {
  private root: any = null;
  private container: HTMLDivElement | null = null;
  public isInitialized: boolean = false;

  async init(config: WidgetConfig): Promise<void> {
    if (this.isInitialized) {
      console.warn('CrymadXWidget is already initialized');
      return;
    }

    if (!config.siteId) {
      throw new Error('CrymadXWidget: siteId is required');
    }

    try {
      // Determine API URL
      const apiUrl = config.apiUrl ||
                     (config.env === 'development' ? 'http://localhost:3001' : DEFAULT_API_URL);

      // Fetch live configuration from backend
      console.log('CrymadXWidget: Fetching live config for siteId:', config.siteId);
      const response = await fetch(`${apiUrl}/api/sites/${config.siteId}/live`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch site config: ${response.status} ${response.statusText}`);
      }

      const liveConfig: PlatformConfig = await response.json();
      console.log('CrymadXWidget: Loaded config:', liveConfig);

      // Create container for widget
      this.container = document.createElement('div');
      this.container.id = 'crymadx-widget-root';
      this.container.style.cssText = `
        position: fixed;
        bottom: 20px;
        right: 20px;
        z-index: 999999;
        font-family: system-ui, -apple-system, sans-serif;
      `;
      document.body.appendChild(this.container);

      // Mount React widget
      this.root = createRoot(this.container);
      this.root.render(
        <React.StrictMode>
          <ChatWidget
            config={liveConfig}
            onEscalate={(text: string, email?: string) => {
              console.log('[Widget] Escalation triggered:', { text, email });
              // Optional: Send escalation event to analytics/backend
              if (email) {
                console.log('[Widget] User email for escalation:', email);
              }
            }}
          />
        </React.StrictMode>
      );

      this.isInitialized = true;
      console.log('CrymadXWidget: Initialized successfully');

    } catch (error) {
      console.error('CrymadXWidget: Initialization failed:', error);
      throw error;
    }
  }

  destroy(): void {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }

    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
      this.container = null;
    }

    this.isInitialized = false;
    console.log('CrymadXWidget: Destroyed');
  }
}

// Create global instance
const widgetInstance = new CrymadXWidget();

// Expose to window
declare global {
  interface Window {
    CrymadXWidget: CrymadXWidgetAPI;
    CRYMADX_CONFIG?: WidgetConfig;
  }
}

window.CrymadXWidget = {
  init: (config: WidgetConfig) => widgetInstance.init(config),
  destroy: () => widgetInstance.destroy(),
  get isInitialized() {
    return widgetInstance.isInitialized;
  }
};

// Auto-initialize if CRYMADX_CONFIG is set
if (window.CRYMADX_CONFIG) {
  window.CrymadXWidget.init(window.CRYMADX_CONFIG).catch((error) => {
    console.error('CrymadXWidget: Auto-initialization failed:', error);
  });
}

export default window.CrymadXWidget;
